import React from 'react';
import { Box, Typography } from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatFlightSchedule } from '../utils/flightScheduleUtils';

const ScheduleColumn = ({ label, schedule, airport }) => {
  if (!schedule) {
    return null;
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        component="div"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontSize: '0.65rem',
          mb: 0.5,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: '#0f172a',
          fontSize: { xs: '1.125rem', sm: '1.25rem' },
          lineHeight: 1.15,
        }}
      >
        {schedule.time}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.secondary',
          fontSize: '0.75rem',
          mt: 0.25,
        }}
      >
        {schedule.date}
      </Typography>
      {airport && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            color: '#475569',
            fontSize: '0.8rem',
            mt: 0.5,
          }}
        >
          {airport}
        </Typography>
      )}
    </Box>
  );
};

const FlightScheduleSummary = ({
  departureTime,
  arrivalTime,
  originCode,
  destinationCode,
  terminal,
  dateLocale,
}) => {
  const { t } = useTranslation();
  const departure = formatFlightSchedule(departureTime, dateLocale);
  const arrival = formatFlightSchedule(arrivalTime, dateLocale);

  if (!departure && !arrival) {
    return null;
  }

  const showTerminal = terminal && terminal !== 'TBD';

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          gap: { xs: 1, sm: 1.5 },
          alignItems: 'start',
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: 1.5,
          bgcolor: '#ffffff',
          border: '1px solid #e2e8f0',
        }}
      >
        <ScheduleColumn
          label={t('itinerary.departure')}
          schedule={departure}
          airport={originCode}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 2.5 }}>
          <ArrowForwardIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
        </Box>
        <ScheduleColumn
          label={t('itinerary.arrival')}
          schedule={arrival}
          airport={destinationCode}
        />
      </Box>
      {showTerminal && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.75, color: 'text.secondary', fontSize: '0.75rem' }}
        >
          {t('itinerary.flightLegs.terminal')}: {terminal}
        </Typography>
      )}
    </Box>
  );
};

export default FlightScheduleSummary;
